import {
  type PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from "react";

import { useSelector } from "react-redux";

import { selectProfileIsHost } from "../../app-main/selectors/profile.selectors";

import type { TaxDetailsContextType } from "./TaxDetails.types";
import { TaxDetailsContext } from "./TaxDetailsContext";
import { useDeleteTaxRegistration } from "./useDeleteTaxRegistration";
import { useTaxRegistrations } from "./useTaxRegistrations";

type TaxDetailsProviderProps = PropsWithChildren;

/**
 * Supplies the guest's tax registrations to the Tax Details tree.
 */
const TaxDetailsProvider = ({ children }: TaxDetailsProviderProps) => {
  const isHost = useSelector(selectProfileIsHost);
  const { isLoading, registrations } = useTaxRegistrations();
  const { deleteRegistration, hasDeleteFailed, isDeleting, resetDeleteError } =
    useDeleteTaxRegistration();
  const [registrationIdToDelete, setRegistrationIdToDelete] = useState<
    string | null
  >(null);

  const closeDeleteModal = useCallback(() => {
    setRegistrationIdToDelete(null);
    resetDeleteError();
  }, [resetDeleteError]);

  const openDeleteModal = useCallback(
    (registrationId: string) => {
      setRegistrationIdToDelete(registrationId);
      resetDeleteError();
    },
    [resetDeleteError],
  );

  /** Leaves the modal open on failure so the guest sees why nothing changed. */
  const deleteVatDetails = useCallback(async () => {
    if (!registrationIdToDelete) {
      return;
    }

    const didDelete = await deleteRegistration(registrationIdToDelete);

    if (didDelete) {
      setRegistrationIdToDelete(null);
    }
  }, [deleteRegistration, registrationIdToDelete]);

  const registrationToDelete =
    registrations.find(
      (registration) => registration.id === registrationIdToDelete,
    ) ?? null;

  const value: TaxDetailsContextType = {
    closeDeleteModal,
    deleteVatDetails,
    hasDeleteFailed,
    isDeleting,
    isHost,
    isLoading,
    openDeleteModal,
    registrationIdToDelete,
    registrationToDelete,
    registrations,
  };

  return (
    <TaxDetailsContext.Provider value={value}>
      {children}
    </TaxDetailsContext.Provider>
  );
};

/**
 * Reads the Tax Details context.
 *
 * @throws When called outside of a `TaxDetailsProvider`.
 */
const useTaxDetails = () => {
  const context = useContext(TaxDetailsContext);

  if (context === null) {
    throw new Error("useTaxDetails must be used within a TaxDetailsProvider.");
  }

  return context;
};

export { TaxDetailsProvider, useTaxDetails };
